/** Arrow keys move the selection within the tablist, as a tablist is expected to. */
const KEY_STEP = { ArrowLeft: -1, ArrowRight: 1 }

/**
 * The panels a tab drives.
 *
 * `aria-controls` is a space-separated id list, and this page needs that: one
 * unit-type pill swaps both the spec column and the drawing beside it, and one
 * clubhouse pill swaps both the plan and its numbered key.
 */
function panelsFor(tab) {
    return (tab.getAttribute('aria-controls') || '')
        .split(/\s+/)
        .filter(Boolean)
        .map((id) => document.getElementById(id))
        .filter(Boolean)
}

/**
 * The tabs a container owns.
 *
 * Filtered by nearest `[data-tabs]` ancestor rather than taken straight off a
 * descendant search: the collection tablist wraps the type pills, so a plain
 * querySelectorAll on the outer container would drive both rows from one
 * selection and leave the pills unable to move on their own.
 */
function ownTabs(root) {
    return Array.from(root.querySelectorAll('[role="tab"]')).filter(
        (tab) => tab.closest('[data-tabs]') === root
    )
}

/**
 * Move the selection to `index`.
 *
 * Both halves of the state live in attributes rather than classes, so the
 * styling in base/plans.css hangs off the same thing a screen reader reads.
 * `tabIndex` is the roving part: the row is one tab stop and the arrows move
 * within it.
 */
function select(tabs, index, { focus = false } = {}) {
    tabs.forEach((tab, i) => {
        const isSelected = i === index
        tab.setAttribute('aria-selected', String(isSelected))
        tab.tabIndex = isSelected ? 0 : -1
        panelsFor(tab).forEach((panel) => panel.setAttribute('aria-hidden', String(!isSelected)))
    })

    if (focus) tabs[index].focus()
}

/**
 * Every tablist on the plans page — the villa collections, the unit types
 * inside each, and the clubhouse floors.
 *
 * Panels are stacked in one grid cell and swapped by opacity (see
 * base/plans.css), so all of them are laid out from the start and none needs
 * re-measuring when its tab is chosen. A no-op off this page.
 */
export function initPlanTabs() {
    document.querySelectorAll('[data-tabs]').forEach((root) => {
        const tabs = ownTabs(root)
        if (!tabs.length) return

        tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => select(tabs, index))

            tab.addEventListener('keydown', (event) => {
                const step = KEY_STEP[event.key]
                if (!step) return

                event.preventDefault()
                // Wrap, so neither end of the row is a dead key press.
                select(tabs, (index + step + tabs.length) % tabs.length, { focus: true })
            })
        })
    })
}
